import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Search, ScanBarcode, X, Check, AlertCircle, FileText, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ══════════════════════════════════════════════════════════════════════════════
// BARCODE SCANNER MODAL COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const BarcodeScannerModal = ({ isOpen, onClose, onScan, products }) => {
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleScan = () => {
    if (!barcode.trim()) return;
    const product = products.find(p => 
      p.code?.toLowerCase() === barcode.toLowerCase() ||
      p.barcode?.toLowerCase() === barcode.toLowerCase() ||
      p.sku?.toLowerCase() === barcode.toLowerCase()
    );
    if (product) {
      onScan(product);
      setSuccess(`Added: ${product.name}`);
      setError('');
      setBarcode('');
      setTimeout(() => setSuccess(''), 2000);
      inputRef.current?.focus();
    } else {
      setError('Item not found. Try again.');
      setSuccess('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleScan(); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-backdropFadeIn">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden animate-modalSlideIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ScanBarcode className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-800">Scan Item</h3>
              <p className="text-[12px] text-slate-500">Scan barcode or type manually</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <div className="relative">
            <input ref={inputRef} type="text" value={barcode}
              onChange={(e) => { setBarcode(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              placeholder="Scan barcode or type and press Enter" autoFocus
            />
            <button onClick={handleScan} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Check className="w-4 h-4" />
            </button>
          </div>
          {success && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-[13px] text-emerald-700 font-medium">{success}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-[13px] text-red-700">{error}</span>
            </div>
          )}
          <p className="mt-4 text-[12px] text-slate-500 text-center">Modal stays open for rapid scanning. Press Enter after each scan.</p>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN CREATE SALES ORDER PAGE - Premium Zoho-Style Design
// ══════════════════════════════════════════════════════════════════════════════
const CreateSalesOrderPage = () => {
  const { businessId, orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomerId = searchParams.get('customer');
  const { activeBusiness } = useBusiness();
  const isEditing = !!orderId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [order, setOrder] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [newlyAddedIndex, setNewlyAddedIndex] = useState(null); // Track newly added item for animation
  const customerSearchRef = useRef(null);
  const itemSearchRef = useRef(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    reference_number: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_shipment_date: '',
    payment_terms: 'net_30',
    delivery_method: '',
    place_of_supply: '',
    items: [],
    order_discount_percent: 0,
    order_discount_amount: 0,
    shipping_charges: 0,
    adjustment: 0,
    customer_notes: '',
    terms_and_conditions: '',
    enable_round_off: true
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchProducts();
      if (isEditing) {
        // Fetch customers first, then order -- order lookup needs customers array populated
        fetchCustomers().then((list) => fetchOrder(list));
      } else {
        fetchCustomers();
      }
    }
  }, [bid, orderId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOrder = async (customersList) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/sales-orders/${orderId}`);
      setOrder(data);
      setFormData(prev => ({ ...prev, ...data }));
      const list = customersList || customers;
      const custId = data.customer_id || data.party_id;
      const cust = list.find(c => (c.party_id || c.id) === custId);
      if (cust) { setSelectedCustomer(cust); setCustomerSearch(cust.name); }
      else if (data.customer_name || data.party_name) { setCustomerSearch(data.customer_name || data.party_name); }
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load order')); }
    finally { setLoading(false); }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/parties', { params: { type: 'customer' } });
      const list = data.parties || data || [];
      setCustomers(list);
      return list;
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load customers')); return []; }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await api.get('/items');
      setProducts(data.data || data || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load items')); }
  };

  // Pre-select customer from query param (e.g. from Customer Detail page)
  useEffect(() => {
    if (!preselectedCustomerId || isEditing || selectedCustomer) return;
    const preselectCustomer = async () => {
      const match = customers.find(c => (c.party_id || c.id) === preselectedCustomerId);
      if (match) { handleCustomerSelect(match); return; }
      if (customers.length > 0) {
        try {
          const { data } = await api.get(`/parties/${preselectedCustomerId}`);
          const party = data.party || data;
          if (party?.name) handleCustomerSelect({ id: party.id, party_id: party.id, name: party.name, phone: party.phone });
        } catch (e) { console.warn('Could not fetch preselected customer:', e); }
      }
    };
    preselectCustomer();
  }, [preselectedCustomerId, customers]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.party_id || customer.id,
      billing_address: customer?.billing_address || customer?.address || prev.billing_address
    }));
  };

  const addItem = (product = null) => {
    if (product) {
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'quantity', (formData.items[existingIndex].quantity || 1) + 1);
        return;
      }
      const lineTotal = 1 * (product.selling_price || product.sale_price || 0);
      const taxRate = product.tax_rate || 0;
      const taxAmt = lineTotal * (taxRate / 100);
      
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex); // Track for animation
        setTimeout(() => setNewlyAddedIndex(null), 400);
        return {
          ...prev,
          items: [...prev.items, {
            item_id: product.id, item_name: product.name, item_code: product.code || '',
            hsn_code: product.hsn_code || '', quantity: 1, unit: product.unit || 'Nos',
            rate: product.selling_price || product.sale_price || 0,
            discount_percent: 0, discount_amount: 0, tax_rate: taxRate,
            tax_amount: Math.round(taxAmt * 100) / 100, amount: Math.round((lineTotal + taxAmt) * 100) / 100
          }]
        };
      });
    } else {
      setFormData(prev => {
        const newIndex = prev.items.length;
        setNewlyAddedIndex(newIndex); // Track for animation
        // Clear animation state after animation completes
        setTimeout(() => setNewlyAddedIndex(null), 400);
        return {
          ...prev,
          items: [...prev.items, {
            item_id: '', item_name: '', item_code: '', hsn_code: '', quantity: 1, unit: 'Nos',
            rate: 0, discount_percent: 0, discount_amount: 0, tax_rate: 0, tax_amount: 0, amount: 0
          }]
        };
      });
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].item_code = product.code || '';
          newItems[index].hsn_code = product.hsn_code || '';
          newItems[index].rate = product.selling_price || product.sale_price || 0;
          newItems[index].unit = product.unit || 'Nos';
          newItems[index].tax_rate = product.tax_rate || 0;
        }
      }

      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      const discountPercent = parseFloat(newItems[index].discount_percent) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;

      const lineTotal = qty * rate;
      const discountAmt = lineTotal * (discountPercent / 100);
      const taxableAmount = lineTotal - discountAmt;
      const taxAmt = taxableAmount * (taxRate / 100);

      newItems[index].discount_amount = Math.round(discountAmt * 100) / 100;
      newItems[index].tax_amount = Math.round(taxAmt * 100) / 100;
      newItems[index].amount = Math.round((taxableAmount + taxAmt) * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.rate || 0)), 0);
  const lineDiscountTotal = formData.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const taxableAmount = subtotal - lineDiscountTotal;
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const beforeRoundOff = taxableAmount + taxTotal + formData.shipping_charges + formData.adjustment;
  const roundOff = formData.enable_round_off ? Math.round(beforeRoundOff) - beforeRoundOff : 0;
  const grandTotal = beforeRoundOff + roundOff;

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!formData.customer_id) {
      errors.customer = 'Customer is required';
    }
    
    if (!formData.order_date) {
      errors.order_date = 'Order date is required';
    }
    
    if (formData.items.length === 0) {
      errors.items = 'At least one item is required';
    } else {
      // Validate each item
      formData.items.forEach((item, index) => {
        if (!item.item_id) {
          errors[`item_${index}_product`] = 'Select a product';
        }
        if (!item.quantity || item.quantity <= 0) {
          errors[`item_${index}_quantity`] = 'Quantity must be > 0';
        }
        if (!item.rate || item.rate <= 0) {
          errors[`item_${index}_rate`] = 'Rate must be > 0';
        }
      });
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (asDraft = false) => {
    // Clear previous errors
    setValidationErrors({});
    
    // Validate form
    if (!validateForm()) {
      // Build error message
      const errorMessages = Object.values(validationErrors);
      if (errorMessages.length > 0) {
        setError(errorMessages.join('. '));
      }
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = { ...formData, save_as_draft: asDraft };

      const { data } = isEditing
        ? await api.put(`/sales-orders/${orderId}`, payload)
        : await api.post('/sales-orders', payload);

      setSuccess('Sales order saved successfully!');
      setTimeout(() => navigate(`/business/${bid}/sales-orders/${data.order?.id || orderId}`), 1000);
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save order'));
    }
    finally { setSaving(false); }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* ══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Commercial Grade Design
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* LEFT: Back Button + Title */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/sales-orders`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">
                  {isEditing ? 'Edit Sales Order' : 'New Sales Order'}
                </h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Create and manage customer orders</p>
              </div>
            </div>
            
            {/* RIGHT: Action Buttons */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleSave(true)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <FileText className="w-4 h-4" /> Save Draft
              </button>
              <button 
                onClick={() => handleSave(false)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
              >
                <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Confirm Order'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA - Premium Layout
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-12 lg:col-span-8 space-y-4">
              
              {/* CUSTOMER DETAILS Card - Premium Style */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <Search className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Customer Details</h2>
                    <p className="text-[11px] text-slate-500">Search and select customer</p>
                  </div>
                </div>
                
                <div className="relative" ref={customerSearchRef}>
                  <label className={`block text-[12px] font-medium mb-2 ${validationErrors.customer ? 'text-red-500' : 'text-slate-600'}`}>
                    Customer {validationErrors.customer && <span className="text-red-500 ml-1">• Required</span>}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text" value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50 border-2 rounded-xl text-[14px] focus:bg-white focus:ring-0 focus:outline-none transition-all duration-200 ${
                        validationErrors.customer 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-slate-200 focus:border-blue-500'
                      }`}
                      placeholder="Search by name, phone, or email..."
                    />
                  </div>
                  
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {filteredCustomers.slice(0, 8).map((customer, idx) => {
                        const contact = [customer.mobile, customer.email].filter(Boolean).join(' • ');
                        return (
                          <div key={customer.party_id || customer.id}
                            onClick={() => handleCustomerSelect(customer)}
                            className={`px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{customer.name}</p>
                              {contact && <p className="text-[12px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedCustomer && (() => {
                  const selContact = [selectedCustomer.mobile, selectedCustomer.email].filter(Boolean).join(' • ');
                  return (
                    <div className="mt-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{selectedCustomer.name}</p>
                          {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                        </div>
                        <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setFormData(prev => ({ ...prev, customer_id: '' })); }}
                          className="text-slate-400 hover:text-slate-600 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ITEMS Card - Premium Design */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Order Items</h2>
                      <p className="text-[11px] text-slate-500">{formData.items.length} item{formData.items.length !== 1 ? 's' : ''} added</p>
                    </div>
                  </div>
                </div>

                {/* Quick Add Row - Premium */}
                <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-11 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-[14px] focus:border-purple-500 focus:ring-0 focus:outline-none transition-all duration-200"
                      placeholder="Search items by name or code..."
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map((product, idx) => (
                          <div key={product.id} onClick={() => addItem(product)}
                            className={`px-4 py-3 hover:bg-purple-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[12px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} — type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setShowScanner(true)}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <ScanBarcode className="w-4 h-4" /> Scan
                  </button>
                  <button onClick={() => addItem()}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]">
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>

                {/* Items Table - Premium */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[14px] font-medium text-slate-600">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Search above or click &quot;Add Line&quot; to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">HSN/SAC</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Unit</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Price</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Disc %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Tax %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                          <th className="px-4 py-3 w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {formData.items.map((item, index) => (
                          <tr key={`item-${index}-${item.item_id || 'new'}`} 
                              className={`hover:bg-slate-50/80 transition-colors duration-150 ${
                                index === newlyAddedIndex ? 'animate-lineItemSlideIn animate-rowHighlight' : ''
                              }`}>
                            <td className="px-4 py-3 text-[13px] text-slate-500 font-medium">{index + 1}</td>
                            <td className="px-4 py-3">
                              <select value={item.item_id} onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-purple-500 focus:bg-white transition-all">
                                <option value="">Select Item</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={item.hsn_code || ''} onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                                className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-purple-500 focus:bg-white transition-all" placeholder="—" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-purple-500 focus:bg-white transition-all" min="0" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-center focus:outline-none focus:border-purple-500 focus:bg-white transition-all" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.rate} onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-purple-500 focus:bg-white transition-all" min="0" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.discount_percent} onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-purple-500 focus:bg-white transition-all" min="0" max="100" placeholder="%" />
                            </td>
                            <td className="px-4 py-3">
                              <input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-16 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-right focus:outline-none focus:border-purple-500 focus:bg-white transition-all" min="0" placeholder="%" />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button onClick={() => removeItem(index)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 active:scale-95">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* TERMS & NOTES Card - Premium */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Terms & Notes</h2>
                    <p className="text-[11px] text-slate-500">Additional information</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Public Notes (visible on order)</label>
                    <textarea value={formData.customer_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none resize-none transition-all duration-200"
                      rows={3} placeholder="Add notes for customer..."
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Payment Terms</label>
                    <select value={formData.payment_terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none transition-all duration-200">
                      <option value="due_on_receipt">Due on Receipt</option>
                      <option value="net_15">Net 15</option>
                      <option value="net_30">Net 30</option>
                      <option value="net_45">Net 45</option>
                      <option value="net_60">Net 60</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-12 lg:col-span-4 space-y-4">
              
              {/* ORDER DETAILS Card - Premium Style */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Order Details</h2>
                    <p className="text-[11px] text-slate-500">Configure order settings</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Order Number</label>
                    <input type="text" value={order?.order_number || 'Auto-generated'} disabled
                      className="w-full px-4 py-2.5 bg-slate-100 border-2 border-slate-200 rounded-xl text-[14px] text-slate-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-2">Order Date</label>
                      <div className="relative">
                        <input type="date" value={formData.order_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Shipment Date</label>
                      <input type="date" value={formData.expected_shipment_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expected_shipment_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Place of Supply</label>
                    <input type="text" value={formData.place_of_supply}
                      onChange={(e) => setFormData(prev => ({ ...prev, place_of_supply: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="State (for tax calculation)"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Reference / PO Number</label>
                    <input type="text" value={formData.reference_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Customer PO or reference"
                    />
                  </div>
                </div>
              </div>

              {/* SUMMARY Card - Premium Green Gradient */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 shadow-sm border border-emerald-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Order Summary</h2>
                    <p className="text-[11px] text-slate-500">Final amounts</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Taxable Amount</span>
                    <span className="text-slate-800">₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600 font-medium">+ ₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-emerald-200 pt-4 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[20px] font-bold text-emerald-600">
                        ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                    <input type="checkbox" checked={formData.enable_round_off}
                      onChange={(e) => setFormData(prev => ({ ...prev, enable_round_off: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-[12px] text-slate-600 group-hover:text-slate-800 transition-colors">Enable round off</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)}
        onScan={(product) => addItem(product)} products={products}
      />
    </div>
  );
};

export default CreateSalesOrderPage;
